import type { BloxSeoHead } from '@bagelink/blox'

export const SITE_ORIGIN = 'https://www.thetorah.com'

function text(value: unknown): string {
	return typeof value === 'string' ? value.replace(/<[^>]*>/g, '').replace(/[<>]/g, '').trim() : ''
}

/** Only authored HTTP(S) URLs are canonical candidates, never executable URLs. */
export function canonicalUrl(path: string, editorial?: unknown): string {
	const fallback = new URL(path.startsWith('/') && !path.startsWith('//') ? path : '/', SITE_ORIGIN)
	fallback.search = ''
	fallback.hash = ''
	const candidate = text(editorial)
	if (!candidate || (!/^https?:\/\//i.test(candidate) && !/^\/(?!\/)/.test(candidate))) return fallback.href
	try {
		const url = new URL(candidate, SITE_ORIGIN)
		if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return fallback.href
		if (['thetorah.com', 'www.thetorah.com'].includes(url.hostname)) {
			url.protocol = 'https:'
			url.host = 'www.thetorah.com'
		}
		url.hash = ''
		return url.href
	} catch {
		return fallback.href
	}
}

function isoDate(value: unknown): string | undefined {
	const source = text(value)
	if (!source) return undefined
	const date = new Date(source)
	return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

function imageUrl(value: unknown): string | undefined {
	const source = text(value)
	if (!source) return undefined
	try {
		const url = new URL(source, SITE_ORIGIN)
		return ['http:', 'https:'].includes(url.protocol) ? url.href : undefined
	} catch {
		return undefined
	}
}

interface NamedPerson {
	name?: string
	title?: string
}

function honorificName(title: unknown, name: unknown): string {
	return [text(title), text(name)].filter(Boolean).join(' ')
}

function firstPeople(record: Record<string, unknown>, people: NamedPerson[] = []): NamedPerson[] {
	if (people.length) return people
	return Array.isArray(record.authors)
		? record.authors.filter((person): person is NamedPerson => !!person && typeof person === 'object')
		: []
}

function byline(record: Record<string, unknown>, people: NamedPerson[] = []): string {
	const person = firstPeople(record, people)[0]
	return person ? honorificName(person.title, person.name) : ''
}

/** Webflow author template: `{university} {name} | TheTorah.com`, description is the institution. */
export function authorSeo(record: Record<string, unknown>, path: string): BloxSeoHead {
	const name = text(record.name)
	const label = honorificName(record.title, name)
	const title = text(record.titleTag) || (label ? `${label} | TheTorah.com` : 'TheTorah.com')
	const description = text(record.metaDescription) || text(record.institution) || undefined
	return {
		title,
		description,
		image: imageUrl(record.image),
		ogType: 'profile',
		structuredData: { '@context': 'https://schema.org', '@type': 'Person', name, url: canonicalUrl(path) },
	}
}

export function articleSeo(
	record: Record<string, unknown>,
	path: string,
	authors: Array<{ name: string, title?: string, link?: string, slug?: string }> = [],
): BloxSeoHead {
	const headline = text(record.title) || text(record.name)
	const title = text(record.titleTag) || (headline ? `${headline} - TheTorah.com` : 'TheTorah.com')
	const summary = text(record.metaDescription) || text(record.teaser) || text(record.subtitle)
	const who = byline(record, authors)
	const description = summary && who && !summary.includes(who) ? `${summary} | ${who}` : summary || who || undefined
	const image = imageUrl(record.image)
	const publishedTime = isoDate(record.publishDate)
	const modifiedTime = isoDate(record.lastUpdated)
	const url = canonicalUrl(path, record.canonical)
	return {
		title,
		description,
		image,
		ogType: 'article',
		publishedTime,
		modifiedTime,
		author: authors.map(author => text(author.name)).filter(Boolean).join(', ') || undefined,
		structuredData: {
			'@context': 'https://schema.org',
			'@type': 'Article',
			'@id': `${url}#article`,
			url,
			headline,
			description,
			...(image ? { image } : {}),
			...(publishedTime ? { datePublished: publishedTime } : {}),
			...(modifiedTime ? { dateModified: modifiedTime } : {}),
			mainEntityOfPage: { '@type': 'WebPage', '@id': url },
			author: authors.filter(author => author.name.trim()).map(author => ({
				'@type': 'Person',
				name: text(author.name),
				...(author.link || author.slug ? {
					url: canonicalUrl(author.link || `/author/${encodeURIComponent(author.slug!)}`),
				} : {}),
			})),
			publisher: { '@type': 'Organization', name: 'TheTorah.com', url: SITE_ORIGIN },
		},
	}
}

const TABS_DESCRIPTION = 'TheTorah.com - Project TABS (Torah And Biblical Scholarship) is an educational organization founded to energize the Jewish people by integrating the study of Torah with the disciplines and findings of academic biblical scholarship.'

const STATIC_SEO: Record<string, { title: string, description?: string }> = {
	'/': {
		title: 'TheTorah.com - Academic Biblical Scholarship',
		description: 'Scholarship on the Torah portion, Jewish holidays, and questions of faith: Torah from Sinai, Torah from Heaven, Biblical criticism and Documentary Hypothesis.',
	},
	'/authors': { title: 'List of Authors - TheTorah.com', description: 'Authors(In alphabetical order)' },
	'/authors/grid': {
		title: 'Our Authors',
		description: 'By understanding the Torah in its historical context, we believe that the border Jewish community will gain a greater appreceation of the Torah’s timeless and diverse messages',
	},
	'/topics': { title: 'Topics - TheTorah.com' },
	'/about': { title: 'About TheTorah.com', description: TABS_DESCRIPTION },
	'/contact': {
		title: 'Contact - TheTorah.com',
		description: 'We love hearing from our readers. If you have feedback on the website or our articles, please be in touch.',
	},
	'/donate': {
		title: 'Support TheTorah.com Today',
		description: 'Sponsor new scholarship on TheTorah.com. Your contribution, no matter the amount, makes a difference in advancing our mission and encourages us to continue our work.',
	},
	'/search': { title: 'Search Results - TheTorah.com' },
	'/latest-essays': { title: 'Latest Essays - TheTorah.com' },
	'/terms': { title: 'Terms of Use - TheTorah.com' },
	'/printable-readers': { title: 'Printable Readers', description: TABS_DESCRIPTION },
	'/interview': { title: 'Interview - TheTorah.com' },
	'/op-ed': { title: 'Op-ed - TheTorah.com' },
	'/blog': { title: 'Blog - TheTorah.com' },
	'/israel-hamas-war': { title: 'Israel-Hamas War - TheTorah.com' },
	'/store/chumash-bereshit': {
		title: 'Chumash Bereshit',
		description: 'The inaugural volume of our Hebrew chumash, התורה ומקורותיה, by Dr. Avi Dentelski',
	},
}

function routePath(path: string): string {
	const bare = path.split('?')[0].split('#')[0]
	return bare.length > 1 && bare.endsWith('/') ? bare.slice(0, -1) : bare || '/'
}

function webHead(title: string, description?: string, image?: string): BloxSeoHead {
	return { title, description: description || undefined, image, ogType: 'website' }
}

function topicSeo(record: Record<string, unknown>): BloxSeoHead {
	return webHead(text(record.name) || text(record.title) || 'TheTorah.com', undefined, imageUrl(record.image))
}

function parashahSeo(record: Record<string, unknown>): BloxSeoHead {
	const name = text(record.name)
	const hebrew = text(record.hebrewName)
	const heading = [name, hebrew].filter(Boolean).join(' ')
	const subject = [name, hebrew].filter(Boolean).join(' - ')
	const portion = text(record.torahPortion)
	const haftarah = text(record.haftarah)
	const readings = [
		portion ? `Torah portion- ${portion}` : '',
		haftarah ? `Haftarah- ${haftarah}` : '',
	].filter(Boolean).join(' - ')
	const description = subject
		? `Essays and Divrei Torah on ${subject}, integrating Historical and Contextual approaches.${readings ? ` | ${readings}` : ''}`
		: undefined
	return webHead(
		heading ? `${heading} - Studies in the Weekly Torah Portion | TheTorah.com` : 'TheTorah.com',
		description,
		imageUrl(record.image),
	)
}

function bookSeo(record: Record<string, unknown>): BloxSeoHead {
	const name = text(record.name) || text(record.title)
	const hebrew = text(record.hebrewName)
	return webHead(
		name ? `${name} - TheTorah.com` : 'TheTorah.com',
		name ? `${hebrew ? `${name} - ${hebrew}` : name}: Divrei Torah and Essays on the Parsha` : undefined,
		imageUrl(record.image),
	)
}

function holidaySeo(record: Record<string, unknown>): BloxSeoHead {
	const name = text(record.name) || text(record.title)
	return webHead(
		text(record.titleTag) || (name ? `${name} - TheTorah.com` : 'TheTorah.com'),
		text(record.metaDescription) || undefined,
		imageUrl(record.ogImage) || imageUrl(record.image),
	)
}

function categorySeo(record: Record<string, unknown>): BloxSeoHead {
	const label = [text(record.name), text(record.hebrewName)].filter(Boolean).join(' ')
	return webHead(label ? `${label} | TheTorah.com` : 'TheTorah.com', undefined, imageUrl(record.image))
}

function seriesSeo(record: Record<string, unknown>): BloxSeoHead {
	const title = text(record.title) || text(record.name) || 'TheTorah.com'
	return webHead(title, byline(record) || text(record.metaDescription) || text(record.teaser) || undefined, imageUrl(record.image))
}

function symposiumSeo(record: Record<string, unknown>): BloxSeoHead {
	const name = text(record.title) || text(record.name)
	const description = [byline(record), text(record.teaser)].filter(Boolean).join(' | ') || text(record.metaDescription) || undefined
	return webHead(name ? `${name} | TheTorah.com` : 'TheTorah.com', description, imageUrl(record.image))
}

function blogSeo(record: Record<string, unknown>): BloxSeoHead {
	const title = text(record.titleTag) || text(record.title) || text(record.name) || 'TheTorah.com'
	return webHead(title, byline(record) || text(record.metaDescription) || text(record.teaser) || undefined, imageUrl(record.image))
}

function suffixedNameSeo(record: Record<string, unknown>, prefix = ''): BloxSeoHead {
	const name = text(record.name) || text(record.title)
	const title = prefix && name ? `${prefix} | ${name} | TheTorah.com` : name ? `${name} - TheTorah.com` : 'TheTorah.com'
	return webHead(title, text(record.metaDescription) || undefined, imageUrl(record.image))
}

/** Page `<title>` and description, using the original Webflow template for that path. */
export function pageSeo(
	path: string,
	record: Record<string, unknown> | null,
	authors: Array<{ name: string, title?: string, link?: string, slug?: string }> = [],
): BloxSeoHead | null {
	const route = routePath(path)
	const fixed = STATIC_SEO[route]
	if (fixed) return webHead(fixed.title, fixed.description)
	const [section, ...rest] = route.split('/').filter(Boolean)
	if (!section || !rest.length || !record) return null
	if (section === 'article' || section === 'interview' || section === 'op-ed') return articleSeo(record, route, authors)
	if (section === 'author') return authorSeo(record, route)
	if (section === 'topic') return topicSeo(record)
	if (section === 'parashah') return parashahSeo(record)
	if (section === 'book' || section === 'books') return bookSeo(record)
	if (section === 'holiday' || section === 'holidays') return holidaySeo(record)
	if (section === 'category' || section === 'categories') return categorySeo(record)
	if (section === 'series') return seriesSeo(record)
	if (section === 'symposium') return symposiumSeo(record)
	if (section === 'blog' || section === 'israel-hamas-war') return blogSeo(record)
	if (section === 'about') return suffixedNameSeo(record, 'About')
	if (section === 'pages') return suffixedNameSeo(record)
	return null
}
